import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    inject,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer2,
    RendererStyleFlags2,
    ViewChildren
} from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { App } from '@df/core/app';
import { WINDOW } from '@df/dom/globals';
import { Renderer } from '@df/ng/renderer';
import { CarouselSlideDirective } from '@df/ui-carousel/carousel-slide.directive';
import { CarouselComponent } from '@df/ui-carousel/carousel.component';
import { Carousel } from '@df/ui-carousel/carousel.interface';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { OnInViewDirective } from '@df/ui/common/on-in-view.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { gsap } from 'gsap';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import Swiper from 'swiper';
import { BottomPanelAreaDirective } from '../../../../../ui/display/bottom-panel/bottom-panel-area.directive';
import { BottomPanelComponent } from '../../../../../ui/display/bottom-panel/bottom-panel.component';
import { TwoDimensionalCarouselBulletsDirective } from './two-dimensional-carousel-bullets.directive';
import { TwoDimensionalCarouselSectionDirective } from './two-dimensional-carousel-section.directive';
import { config } from './two-dimensional-carousel.config';
import { NTwoDimensionalCarousel } from './two-dimensional-carousel.interface';

let uid = 0;

export const cmsTwoDimensionalCarouselUid = () => {
    return uid++;
};

@Component({
    selector: 'page-component-two-dimensional-carousel',
    templateUrl: './two-dimensional-carousel.component.html',
    styleUrl: './two-dimensional-carousel.component.scss',
    standalone: true,
    imports: [
        BottomPanelAreaDirective,
        BottomPanelComponent,
        CommonModule,
        CarouselComponent,
        CarouselSlideDirective,
        DragScrollDirective,
        ICmsClientModule,
        OnInViewDirective,
        SizeClassDirective,
        TranslatePipe,
        TwoDimensionalCarouselBulletsDirective,
        TwoDimensionalCarouselSectionDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentTwoDimensionalCarouselComponent>(config)
export class PageComponentTwoDimensionalCarouselComponent
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit, AfterViewInit, OnDestroy
{
    @ICmsContent({
        type: 'component'
    })
    component!: NTwoDimensionalCarousel.IComponentData;

    readonly uid = `two-dimensional-carousel-${cmsTwoDimensionalCarouselUid()}`;

    /**
     * Allowed component ids
     */
    whitelist = [
        'blog-post-carousel-v1',
        'blog-post-carousel-v2',
        'category-carousel-v1',
        'category-carousel-v2',
        'feature-banner',
        'feature-banner-v2',
        'full-screen-hero-banner',
        'full-width-banner',
        'full-width-banner-v2',
        'full-width-banner-v3',
        'hero-image',
        'multi-column-banner-v1',
        'multi-column-banner-v2',
        'multi-column-banner-v3',
        'product-carousel-v1',
        'product-carousel-v2',
        'video-layout'
    ];

    prevSectionText?: string;
    nextSectionText?: string;
    transition = false;
    creativeEffect = {
        prev: {
            translate: ['-20%', 0, -1]
        },
        next: {
            translate: ['100%', 0, 0]
        }
    };
    creativeItemsEffect = {
        prev: {
            translate: [0, '-20%', -1]
        },
        next: {
            translate: [0, '100%', 0]
        }
    };

    protected isScrolling = false;
    protected intersectionObserver?: IntersectionObserver;
    protected lastIntersectionRatio = 1;
    protected destroyed$ = new Subject<void>();
    protected fromHistory = false;
    protected carousel?: Swiper;
    protected carouselSections?: TwoDimensionalCarouselSectionDirective[];
    protected carouselBullets?: TwoDimensionalCarouselBulletsDirective[];
    protected lastSection?: number;
    protected visualViewportResizing = false;
    protected visualViewportResizeTimeout?: number;

    @SessionStorage('tdc.currentSection')
    currentSection!: number;

    @SessionStorage('tdc.currentItem')
    currentItem!: Record<number, number>;

    carouselVertical: Record<number, Swiper> = {};

    carouselVerticalBullets: Record<number, Swiper> = {};

    @ViewChildren(TwoDimensionalCarouselSectionDirective)
    set sections(value: QueryList<TwoDimensionalCarouselSectionDirective>) {
        this.carouselSections = value.toArray();

        this.updateControls();
    }

    @ViewChildren(TwoDimensionalCarouselBulletsDirective)
    set bullets(value: QueryList<TwoDimensionalCarouselBulletsDirective>) {
        this.carouselBullets = value.toArray();

        this.updateControls();
    }

    protected renderer = inject(Renderer);

    constructor(
        protected app: App,
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        @Inject(WINDOW) protected window: Window
    ) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (this.app.isPlatformBrowser) {
            const updateDebounced = this.timeoutService.debounce(() => this.carousel?.update(), 20);

            this.client.obs.breakpoint.pipe(takeUntil(this.destroyed$)).subscribe(() => updateDebounced());

            this.app.meta.routerEvents.pipe(takeUntil(this.destroyed$)).subscribe(event => {
                if (event instanceof NavigationStart && (event as NavigationStart).navigationTrigger === 'popstate') {
                    this.fromHistory = true;
                }

                if (event instanceof NavigationEnd) {
                    if (!this.fromHistory) {
                        // if not history back then reset
                        this.currentItem = { 0: 0 };
                        this.currentSection = 0;

                        this.reset();
                        this.resetSection();
                    } else {
                        this.goToSection();
                        this.goToItem();
                    }
                }
            });

            this.app.events
                .on('cms-form-field-repeater.expand')
                .pipe(takeUntil(this.destroyed$))
                .subscribe(data => {
                    if (data.component.id === NTwoDimensionalCarousel.ID) {
                        if (data.meta.key === 'sections') {
                            this.goToSection(data.index);
                        } else if (data.meta.key === 'items') {
                            this.goToItem(data.index);
                        }
                    }
                });

            this.intersectionObserver = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        const ratio = Math.round(100 * entry.intersectionRatio) / 100;

                        if (ratio > this.lastIntersectionRatio && ratio >= 0.85 && ratio <= 0.9) {
                            this.scrollInToView();
                        }

                        this.lastIntersectionRatio = ratio;
                    });
                },
                {
                    root: null,
                    rootMargin: '0px',
                    threshold: [0.8, 0.85, 0.9, 0.95]
                }
            );

            this.intersectionObserver.observe(this.elementRef.nativeElement);

            this.window.visualViewport!.addEventListener('resize', () => this.onVisualViewportResize());

            this.detectChanges();
        }
    }

    ngAfterViewInit(): void {
        this.updateControls();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.destroyed$.next();
        this.destroyed$.complete();
        this.intersectionObserver?.disconnect();
        if (this.app.isPlatformBrowser) {
            this.window.visualViewport!.removeEventListener('resize', () => this.onVisualViewportResize());
        }
    }

    get currentSectionItemIndex(): number {
        return this.currentItem ? this.currentItem[this.currentSection] : 0;
    }

    handleAfterInit(params: Carousel.EventsParams['afterInit'], index?: number) {
        const [swiper] = params;

        if (index !== undefined) {
            if (!this.carouselVertical[index]) {
                this.carouselVertical[index] = swiper;

                this.detectChanges();
            }
        } else {
            this.carousel = swiper;

            this.updateNavColor(this.currentSectionItemIndex);
            this.updateNavText();

            this.timeoutService.setTimeout(() => this.runAnimations(), 1000);
        }
    }

    handleActiveIndexChange(params: Carousel.EventsParams['activeIndexChange'], index?: number) {
        const [swiper] = params;
        this.lastSection = this.currentSection;

        if (index !== undefined) {
            if ((swiper.isEnd || swiper.isBeginning) && !(<any>swiper.params.mousewheel).releaseOnEdges) {
                this.timeoutService.setTimeout(() => {
                    (<any>swiper.params.mousewheel).releaseOnEdges = true;

                    swiper.update();
                }, 1000);
            } else if ((<any>swiper.params.mousewheel).releaseOnEdges) {
                (<any>swiper.params.mousewheel).releaseOnEdges = false;
                swiper.update();
            }

            this._currentItem(index, swiper.realIndex);

            this.detectChanges();
        } else {
            this.currentSection = swiper.realIndex;

            this._currentItem(swiper.realIndex, 0);
            this.updateNavText();
        }

        this.updateNavColor(this.currentSectionItemIndex);
    }

    handleSectionChangeTransitionStart() {
        if (!this.transition) {
            this.transition = true;

            this.detectChanges();
        }
    }

    handleSectionChangeTransitionEnd() {
        if (this.transition) {
            this.transition = false;

            this.resetSection(this.lastSection, 1000);

            this.runAnimations();

            this.detectChanges();
        }
    }

    handleItemChangeTransitionEnd() {
        this.runAnimations();
    }

    handleTransitionStart(): void {
        if (this.lastIntersectionRatio >= 0.3 && this.lastIntersectionRatio <= 0.95) {
            this.scrollInToView();
        }
    }

    protected updateControls(): void {
        this.timeoutService.setTimeout(() => {
            if (this.carouselSections && this.carouselBullets) {
                this.carouselSections.forEach((section, index) => {
                    if (section.carousel.swiperRef()!.controller) {
                        section.carousel.swiperRef()!.controller.control = this.carouselBullets?.[index].carousel.swiperRef() as Swiper;
                    }
                });

                this.carouselBullets.forEach((section, index) => {
                    if (section.carousel.swiperRef()!.controller) {
                        section.carousel.swiperRef()!.controller.control = this.carouselSections?.[index].carousel.swiperRef() as Swiper;
                    }
                });

                this.detectChanges();
            }
        }, 500);
    }

    protected goToSection(index = this.currentSection): void {
        if (this.carousel) {
            this.carousel.slideTo(index + 1); // if loop then +1
        }
    }

    protected goToItem(index = this.currentSectionItemIndex): void {
        this.carouselSections?.forEach(section => {
            if (section.index === this.currentSection) {
                section.carousel.swiperRef()!.slideTo(index);
            }
        });
    }

    protected onVisualViewportResize(): void {
        this.visualViewportResizing = true;

        if (this.visualViewportResizeTimeout) {
            clearTimeout(this.visualViewportResizeTimeout);

            this.visualViewportResizeTimeout = undefined;
        }

        this.visualViewportResizeTimeout = this.timeoutService.setTimeout(() => {
            this.visualViewportResizing = false;
        }, 500);
    }

    protected scrollInToView(): void {
        if (this.app.isPlatformBrowser && !this.app.client.isCms && !this.isScrolling && !this.visualViewportResizing) {
            const rootElement = this.app.client.getRootElement();
            let target: HTMLElement | Window = rootElement;

            if (rootElement.tagName === 'HTML') {
                target = window;
            }

            const y = this.component?.pageTop === undefined || this.component?.pageTop ? 0 : this.elementRef.nativeElement;

            this.isScrolling = true;

            gsap.to(target, {
                scrollTo: { y, offsetY: 0, autoKill: false },
                duration: 0.25,
                onComplete: () => {
                    this.isScrolling = false;
                }
            });
        }
    }

    protected reset(): void {
        if (this.carousel) {
            this.carousel.slideTo(0, 0);
        }
    }

    protected resetSection(sectionIndex = this.currentSection, delay = 0): void {
        if (sectionIndex !== undefined && this.carouselSections) {
            this.timeoutService.setTimeout(() => {
                this.carouselSections?.filter(section => {
                    if (section.index === sectionIndex) {
                        section.carousel.swiperRef()!.slideTo(0, 0);

                        this._currentItem(sectionIndex, 0);
                    }
                });
            }, delay);
        }
    }

    protected updateNavColor(index = 0): void {
        if (this.component?.sections[this.currentSection]?.items[index]) {
            const color = this.component?.sections[this.currentSection]?.items[index].color;

            this.renderer.setStyle(this.elementRef.nativeElement, '--tdc-color', color ? `${color}` : null, RendererStyleFlags2.DashCase);
        }
    }

    protected updateNavText(): void {
        if (this.component?.sections[this.currentSection]) {
            const numSections = this.component?.sections.length;
            const prevIndex = this.currentSection > 0 ? this.currentSection - 1 : numSections - 1;
            const nextIndex = this.currentSection < numSections - 1 ? this.currentSection + 1 : 0;
            const prevSection = this.component?.sections[prevIndex];
            const nextSection = this.component?.sections[nextIndex];

            this.prevSectionText = prevSection?.name;
            this.nextSectionText = nextSection?.name;

            this.detectChanges();
        }
    }

    protected runAnimations(): void {
        if (this.carouselSections) {
            this.carouselSections.forEach(section => {
                if (section.index === this.currentSection) {
                    section.runAnimations();
                }
            });
        }
    }

    private _currentItem(section = this.currentSection, index = 0) {
        const tmp = {};
        tmp[section] = index;

        this.currentItem = { ...this.currentItem, ...tmp };
    }
}
